import { useParams } from "react-router";
import { Download, FileWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fileIconFor } from "@/components/files/files-types";
import { formatFileSize } from "@/lib/files-format";
import { trpc } from "@/lib/trpc";

/** Página pública (sem autenticação) pra quem recebeu um link de compartilhamento — só metadado
 *  mínimo + botão de baixar, resolvidos por token via file.resolveShareLink/downloadViaShareLink
 *  (publicProcedure). Fora do RequireAuth de propósito: quem abre esse link pode não ter conta. */
export function SharedFile() {
  const { token } = useParams<{ token: string }>();
  const utils = trpc.useUtils();
  const shareQuery = trpc.file.resolveShareLink.useQuery({ token: token ?? "" }, { enabled: !!token, retry: false });

  async function handleDownload() {
    if (!token) return;
    try {
      const { url } = await utils.client.file.downloadViaShareLink.query({ token });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Não foi possível baixar o arquivo.");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-4 dark:bg-background">
      <div className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm dark:border-border dark:bg-card">
        <div className="mb-4 flex justify-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-600 text-lg font-bold text-white">
            B
          </div>
        </div>

        {shareQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
          </div>
        ) : shareQuery.isError || !shareQuery.data ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <FileWarning className="h-8 w-8 text-zinc-300" />
            <p className="text-sm font-medium text-zinc-900 dark:text-foreground">Link inválido ou expirado</p>
            <p className="text-xs text-zinc-500 dark:text-muted-foreground">
              Peça pra quem compartilhou gerar um novo link.
            </p>
          </div>
        ) : (
          (() => {
            const Icon = fileIconFor(shareQuery.data.contentType);
            return (
              <div className="flex flex-col items-center gap-3 py-4">
                <Icon className="h-10 w-10 text-zinc-400 dark:text-muted-foreground" />
                <p className="max-w-full truncate text-sm font-medium text-zinc-900 dark:text-foreground" title={shareQuery.data.name}>
                  {shareQuery.data.name}
                </p>
                <p className="text-xs text-zinc-500 dark:text-muted-foreground">{formatFileSize(shareQuery.data.size)}</p>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="mt-2 inline-flex h-10 items-center gap-2 rounded-md bg-brand-600 px-5 text-sm font-semibold text-white hover:bg-brand-700"
                >
                  <Download className="h-4 w-4" />
                  Baixar arquivo
                </button>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
}
